import { GAMES, type GameId, type MatchEndReason } from '@sf72/shared';
import { useMatch } from '../store/match.js';
import { Button, Card } from './primitives.js';
import { closeApp } from '../telegram.js';

const REASON_TEXT: Partial<Record<MatchEndReason, string>> = {
  resign: 'Taslim bo‘lish bilan',
  timeout: 'Vaqt tugadi',
  disconnect: 'Aloqa uzildi',
  draw_agreed: 'Kelishuv bo‘yicha durrang',
  aborted: 'O‘yin to‘xtatildi',
};

export function ResultCard() {
  const match = useMatch((s) => s.match);
  const result = useMatch((s) => s.result);
  const pair = useMatch((s) => s.pair);
  const yourSeat = useMatch((s) => s.yourSeat);
  const offer = useMatch((s) => s.offer);
  const offerRematch = useMatch((s) => s.offerRematch);
  const answerRematch = useMatch((s) => s.answerRematch);

  if (!match || !result || match.status !== 'finished') return null;

  const game = GAMES[match.gameId as GameId];

  /**
   * The couple games are played on the same side.
   *
   * Nothing was won, so there is no result to announce — and announcing one
   * anyway is worse than saying nothing. Two people who just answered the daily
   * question together were being told they drew, under a handshake, with a
   * rating box and a scoreboard beside it.
   */
  const together = game.cooperative === true;

  const drew = result.winnerSeat === null;
  const won = result.winnerSeat === yourSeat;
  const delta = yourSeat === null ? 0 : (result.ratingDelta[yourSeat] ?? 0);

  const headline = together
    ? '💛 Tugadi'
    : drew
      ? '🤝 Durrang'
      : won
        ? '🏆 Yutdingiz!'
        : '😔 Yutqazdingiz';

  const reason = together ? undefined : REASON_TEXT[result.reason];

  const rematchFromOpponent = offer?.kind === 'rematch' && offer.fromSeat !== yourSeat;
  const rematchPending = offer?.kind === 'rematch' && offer.fromSeat === yourSeat;

  return (
    <Card className="flex flex-col gap-3 text-center">
      <div>
        <div className="text-[22px] font-semibold">{headline}</div>
        {reason && <div className="mt-1 text-[13px] text-hint">{reason}</div>}
      </div>

      {!together && (
        <div className="flex justify-center gap-6 text-[14px]">
          <div>
            <div className="text-hint">Reyting</div>
            <div className="font-semibold tabular-nums">
              {delta > 0 ? `+${delta}` : delta}
            </div>
          </div>
          {pair && (
            <>
              <div>
                <div className="text-hint">{game.name}</div>
                <div className="font-semibold tabular-nums">
                  {pair.game.wins}:{pair.game.losses}:{pair.game.draws}
                </div>
              </div>
              <div>
                <div className="text-hint">Umumiy</div>
                <div className="font-semibold tabular-nums">
                  {pair.overall.wins}:{pair.overall.losses}:{pair.overall.draws}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {result.wagerText && (
        <p className="rounded-xl bg-bg px-3 py-2 text-[14px]">
          🎁 Garov: <i>{result.wagerText}</i>
        </p>
      )}

      {rematchFromOpponent ? (
        <div className="flex gap-2">
          <Button onClick={() => answerRematch(true)} full>
            🔄 Yana o‘ynaymiz
          </Button>
          <Button variant="secondary" onClick={() => answerRematch(false)} full>
            Yo‘q
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button onClick={offerRematch} disabled={rematchPending} full>
            {rematchPending ? 'Javob kutilyapti…' : '🔄 Yana o‘ynash'}
          </Button>
          <Button variant="secondary" onClick={closeApp} full>
            Yopish
          </Button>
        </div>
      )}
    </Card>
  );
}
